import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDailyPeak,
  getRajasthanMonthlyPeakDiscom,
  getDailyDiscomDrawalSchedule,
  getMonthlyDiscomDrawalSchedule,
  getDailyTotalDrawalSchedule,
  getMonthlyTotalDrawalSchedule,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];
const TYPE = ["Schedule", "Drawal"];
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];
const GROUP_BY_ARRAY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];

// Helper function to get month with year
function getMonthWithYear(month: number, year: number): string {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const yearShort = year.toString().slice(-2);
  return `${monthNames[month - 1]}-${yearShort}`;
}

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) {
    return { month: 0, year: 0 };
  }
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) {
    return { month: 0, year: 0 };
  }
  const [monthStr, yearStr] = parts;
  const month = monthNames.indexOf(monthStr) + 1;
  const year = 2000 + parseInt(yearStr);
  return { month, year };
}

// Helper to parse "MM-YYYY" format from API to "Mon-YY" format
function parseApiMonthYear(apiMonthYear: string | undefined): string {
  if (!apiMonthYear) {
    return "";
  }
  const parts = apiMonthYear.split("-");
  if (parts.length !== 2) {
    return "";
  }
  const [month, year] = parts;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = parseInt(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return "";
  }
  const yearShort = year.slice(-2);
  return `${monthNames[monthIndex]}-${yearShort}`;
}

interface RajasthanPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
}

export function RajasthanPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
}: RajasthanPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);

  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string
    ) => {
      return {
        dataKey: `${key}_${suffix}`,
        name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      };
    },
    [selectedChartType]
  );

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        console.error("Invalid date range");
        setData([]);
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      let allData: any[] = [];

      if (selectedGroupKeys === "RVPN Drawal") {
        // Fetch RVPN Drawal data
        if (selectedXaxis.key === "month") {
          const monthData = await getRajasthanMonthlyPeakDiscom(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(monthData)) {
            const processedData = monthData.map((item) => ({
              ...item,
              month: getMonthWithYear(item.month, item.year),
            }));
            allData = processedData;
          }
        } else {
          const dailyData = await getRajasthanDailyPeak(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(dailyData)) {
            allData = dailyData;
          }
        }
      } else if (selectedGroupKeys === "Total Discom Load") {
        // Fetch Total Discom Load data
        if (selectedXaxis.key === "month") {
          const apiResponse: any = await getMonthlyTotalDrawalSchedule(
            startDateStr,
            endDateStr
          );

          // Extract data array from API response
          const totalMonthData = (apiResponse?.data ||
            apiResponse ||
            []) as any[];

          if (Array.isArray(totalMonthData) && totalMonthData.length > 0) {
            const processedData = totalMonthData.map((item: any) => {
              // Parse the formatted_data to extract morning and evening peaks
              const morningData = item.formatted_data?.find(
                (fd: any) => fd.time_period === "Morning"
              );
              const eveningData = item.formatted_data?.find(
                (fd: any) => fd.time_period === "Evening"
              );

              const monthValue = parseApiMonthYear(item.month_year);
              const yearValue = item.month_year
                ? parseInt(item.month_year.split("-")[1])
                : new Date().getFullYear();

              return {
                ...item,
                month: monthValue,
                year: yearValue,
                peak_schedule_mw: item.monthly_peak_schedule_mw || 0,
                peak_drawal_mw: item.monthly_peak_drawal_mw || 0,
                morning_peak_schedule_mw: morningData?.max_schedule || 0,
                morning_peak_drawal_mw: morningData?.max_drawal || 0,
                evening_peak_schedule_mw: eveningData?.max_schedule || 0,
                evening_peak_drawal_mw: eveningData?.max_drawal || 0,
                average_schedule_mw: item.avg_monthly_schedule_mw || 0,
                average_drawal_mw: item.avg_monthly_drawal_mw || 0,
              };
            });

            allData = processedData;
          }
        } else {
          // Use getDailyTotalDrawalSchedule for daily total discom load
          const apiResponse: any = await getDailyTotalDrawalSchedule(
            startDateStr,
            endDateStr
          );

          // Extract data array from API response
          const totalDailyData = (apiResponse?.data ||
            apiResponse ||
            []) as any[];

          if (Array.isArray(totalDailyData) && totalDailyData.length > 0) {
            const processedData = totalDailyData.map((item: any) => {
              // Parse the formatted_data to extract morning and evening peaks
              const morningData = item.formatted_data?.find(
                (fd: any) => fd.time_period === "Morning"
              );
              const eveningData = item.formatted_data?.find(
                (fd: any) => fd.time_period === "Evening"
              );

              return {
                ...item,
                peak_schedule_mw: item.daily_peak_schedule_mw || 0,
                peak_drawal_mw: item.daily_peak_drawal_mw || 0,
                morning_peak_schedule_mw: morningData?.max_schedule || 0,
                morning_peak_drawal_mw: morningData?.max_drawal || 0,
                evening_peak_schedule_mw: eveningData?.max_schedule || 0,
                evening_peak_drawal_mw: eveningData?.max_drawal || 0,
                average_schedule_mw: item.avg_daily_schedule_mw || 0,
                average_drawal_mw: item.avg_daily_drawal_mw || 0,
              };
            });

            allData = processedData;
          }
        }
      } else {
        // Fetch Discom Load data
        if (selectedXaxis.key === "month") {
          const discomMonthData = await getMonthlyDiscomDrawalSchedule(
            startDateStr,
            endDateStr
          );

          if (Array.isArray(discomMonthData)) {
            const processedData = discomMonthData.map((item) => ({
              ...item,
              month: getMonthWithYear(item.month, item.year),
              peak_schedule_mw: item.peak_schedule_mw || 0,
              peak_drawal_mw: item.peak_drawal_mw || 0,
              morning_peak_schedule_mw: item.morning_peak_schedule_mw || 0,
              morning_peak_drawal_mw: item.morning_peak_drawal_mw || 0,
              evening_peak_schedule_mw: item.evening_peak_schedule_mw || 0,
              evening_peak_drawal_mw: item.evening_peak_drawal_mw || 0,
              average_schedule_mw: item.average_schedule_mw || 0,
              average_drawal_mw: item.average_drawal_mw || 0,
            }));

            allData = processedData;
          }
        } else {
          const discomDailyData = await getDailyDiscomDrawalSchedule(
            startDateStr,
            endDateStr
          );

          if (Array.isArray(discomDailyData)) {
            const processedData = discomDailyData.map((item) => ({
              ...item,
              peak_schedule_mw: item.daily_peak_schedule_mw || 0,
              peak_drawal_mw: item.daily_peak_drawal_mw || 0,
              average_schedule_mw: item.avg_daily_schedule_mw || 0,
              average_drawal_mw: item.avg_daily_drawal_mw || 0,
            }));

            allData = processedData;
          }
        }
      }

      let newData = allData;

      if (selectedGroupKeys !== "Discom Load") {
        const dataMap = new Map<string, any>();
        allData.forEach((item: any) => {
          const discomPart = item.discom ? `_${item.discom}` : "";
          const uniqueKey =
            selectedXaxis.key === "month"
              ? `${item.month}_${item.year}${discomPart}`
              : `${item.date}${discomPart}`;

          if (!dataMap.has(uniqueKey)) {
            dataMap.set(uniqueKey, item);
          }
        });
        newData = Array.from(dataMap.values());
      }

      setData(newData);

      setUniqueDate(
        Array.from(
          new Set(
            newData
              .map((item: any) => item.date)
              .filter((date: any) => date !== undefined)
              .sort(
                (a: string, b: string) =>
                  new Date(a).getTime() - new Date(b).getTime()
              )
          )
        )
      );
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key, selectedGroupKeys]);

  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    if (
      selectedGroupKeys !== "RVPN Drawal" &&
      selectedGroupKeys !== "Total Discom Load"
    ) {
      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredData = filteredData.filter((item) =>
          selectedDiscom.includes(item.discom)
        );
      }
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date)
      );
    }

    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        // Ensure both items have month property
        if (!a.month || !b.month) {
          return 0;
        }
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        if (aMonthYear.year !== bMonthYear.year) {
          return aMonthYear.year - bMonthYear.year;
        }
        return aMonthYear.month - bMonthYear.month;
      } else {
        // Ensure both items have date property
        if (!a.date || !b.date) {
          return 0;
        }
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
    const convertedData = filteredData.map((item) => {
      return {
        ...item,
        peak_mw: item.peak_mw ? item.peak_mw * conversionFactor : 0,
        morning_peak_mw: item.morning_peak_mw
          ? item.morning_peak_mw * conversionFactor
          : 0,
        evening_peak_mw: item.evening_peak_mw
          ? item.evening_peak_mw * conversionFactor
          : 0,
        average_mw: item.average_mw
          ? item.average_mw * conversionFactor
          : undefined,
        peak_schedule_mw: item.peak_schedule_mw
          ? item.peak_schedule_mw * conversionFactor
          : 0,
        peak_drawal_mw: item.peak_drawal_mw
          ? item.peak_drawal_mw * conversionFactor
          : 0,
        morning_peak_schedule_mw: item.morning_peak_schedule_mw
          ? item.morning_peak_schedule_mw * conversionFactor
          : 0,
        morning_peak_drawal_mw: item.morning_peak_drawal_mw
          ? item.morning_peak_drawal_mw * conversionFactor
          : 0,
        evening_peak_schedule_mw: item.evening_peak_schedule_mw
          ? item.evening_peak_schedule_mw * conversionFactor
          : 0,
        evening_peak_drawal_mw: item.evening_peak_drawal_mw
          ? item.evening_peak_drawal_mw * conversionFactor
          : 0,
        average_schedule_mw: item.average_schedule_mw
          ? item.average_schedule_mw * conversionFactor
          : 0,
        average_drawal_mw: item.average_drawal_mw
          ? item.average_drawal_mw * conversionFactor
          : 0,
      };
    });

    let aggregationKeys = [];
    let groupByKeys: string[] = [];

    if (selectedGroupKeys === "RVPN Drawal") {
      aggregationKeys = ["peak_mw", "morning_peak_mw", "evening_peak_mw"];
      groupByKeys = [];
    } else if (selectedGroupKeys === "Discom Load") {
      aggregationKeys = [
        "peak_schedule_mw",
        "peak_drawal_mw",
        "morning_peak_schedule_mw",
        "morning_peak_drawal_mw",
        "evening_peak_schedule_mw",
        "evening_peak_drawal_mw",
        "average_schedule_mw",
        "average_drawal_mw",
      ];
      groupByKeys = ["discom"];
    } else {
      // Total Discom Load
      aggregationKeys = [
        "peak_schedule_mw",
        "peak_drawal_mw",
        "morning_peak_schedule_mw",
        "morning_peak_drawal_mw",
        "evening_peak_schedule_mw",
        "evening_peak_drawal_mw",
        "average_schedule_mw",
        "average_drawal_mw",
      ];
      groupByKeys = [];
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: ``,
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      const suffixes = [
        { suffix: "peak_mw", displayName: "Day Peak" },
        { suffix: "morning_peak_mw", displayName: "Morning Peak" },
        { suffix: "evening_peak_mw", displayName: "Evening Peak" },
      ];
      return suffixes.flatMap(({ suffix, displayName }) =>
        chartKeys.map((key, index) =>
          createChartObject(key, index, suffix, displayName, suffix)
        )
      );
    } else {
      const peakTypes = [
        { field: "peak", displayName: "Day Peak" },
        { field: "morning_peak", displayName: "Morning Peak" },
        { field: "evening_peak", displayName: "Evening Peak" },
      ];

      const legends: any[] = [];

      chartKeys.forEach((key, keyIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          peakTypes.forEach((peak) => {
            const fieldSuffix = `${peak.field}_${suffix}_mw`;
            const legendDisplayName = `${key} ${peak.displayName} ${displayName}`;
            legends.push({
              dataKey: `${key}_${fieldSuffix}`,
              name: legendDisplayName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            });
          });
        });
      });

      return legends;
    }
  }, [
    selectedGroupKeys,
    chartKeys,
    selectedSuffix,
    selectedChartType,
    createChartObject,
  ]);
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);
    
  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Rajasthan Load Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => onUnitChange(item)}
          />
          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Discom"
              items={DISCOM.map((item) => item.label)}
              selectedItems={selectedDiscom}
              onSelect={(item, index) => {}}
              setSelectedItems={(item) => {
                if (selectedGroupKeys === "Total Discom Load") {
                  dispatch(setLoadingFilterName(""));
                  return;
                }
                onDiscomChange(item);
              }}
            />
          )}
          <FilterMenu
            title="Group By"
            items={GROUP_BY_ARRAY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />
          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={TYPE}
              selectedItems={selectedSuffix.map(
                (item) => item?.displayName || "",
              )}
              setSelectedItems={(selected: string[]) => {
                onTypeChange(selected);
              }}
            />
          )}
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">
              Rajasthan Load Peak Analysis ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV({
                      ...commonSummaryProps,
                    });
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(commonSummaryProps);
                  } else if (dataTab.label === "Table") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={legends as any}
              onLegendSelect={handleLegendSelect} // ← from hook
              defaultSelectedLegends={selectedLegends} //
              xDataKey={xDataKey}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...commonSummaryProps} />
          )}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1. Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2. Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
